import { TunaDialog, DialogContent, DialogActions } from '../primitives/dialog.js'
import { Button } from '../primitives/button.js'
import { LinearProgress } from '../primitives/linear-progress.js'
import type { FC } from 'react'
import React, { useId } from 'react'
import { DialogTitle } from './dialog-title.js'
import moduleStyles from './confirm-dialog.module.css'

export interface ConfirmDialogProps {
  className?: string
  open: boolean
  title?: string
  /** Busy - disable all inputs / buttons and show a progress bar */
  busy?: boolean
  /** OK button is enabled */
  enabled?: boolean
  okText?: string
  cancelText?: string
  text: string
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  /**
   * Focus the cancel button, not OK: when OK is the destructive choice and cancel the safe one, the safe one is the
   * default (e.g. "Close without copying?": Close / Keep open, GH #2393). Escape always cancels.
   */
  focusCancel?: boolean
  /**
   * No × in the title. Use it when the confirm button is itself named "Close": a × also named Close that cancels would
   * mean two Close buttons doing opposite things. Escape still cancels (GH #2393).
   */
  hideCloseButton?: boolean
  children?: React.ReactNode
  /** @deprecated use onCancel instead */
  onClose?: (ev: React.SyntheticEvent) => void
  onCancel(ev: React.SyntheticEvent): void
  onConfirm(ev: React.SyntheticEvent): void
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  children,
  text,
  busy = false,
  open,
  okText = 'OK',
  cancelText = 'Cancel',
  enabled = true,
  onConfirm,
  onCancel,
  title = 'Confirm',
  maxWidth,
  focusCancel = false,
  hideCloseButton = false,
}) => {
  const titleId = useId()
  if (!open) {
    return null
  }
  return (
    <TunaDialog
      open={open}
      onClose={() => onCancel({} as React.SyntheticEvent)}
      maxWidth={maxWidth}
      aria-labelledby={titleId}
    >
      <div onClick={ev => ev.stopPropagation()}>
        <DialogTitle onClose={hideCloseButton ? undefined : onCancel} id={titleId}>
          {title}
        </DialogTitle>
        <DialogContent>
          <p id={`${titleId}-description`}>{text}</p>
          {children}
          {busy ? <LinearProgress className={moduleStyles.progressBar} /> : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onCancel} autoFocus={focusCancel}>
            {cancelText}
          </Button>
          <Button disabled={busy || !enabled} onClick={onConfirm} autoFocus={!focusCancel}>
            {okText}
          </Button>
        </DialogActions>
      </div>
    </TunaDialog>
  )
}

export default ConfirmDialog
